import { Menu, ArrowBackIos } from '@mui/icons-material';
import { Box, IconButton, LinearProgress, Typography, Button } from '@mui/material';
import { capitalize } from 'helpers/format';
import React, { Dispatch, SetStateAction } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'store/hooks';
import User from 'types/entities/User';
import UserTopBar from './UserTopBar';
import { setPrevSearchParamsAction } from 'store/actions/managementActions';
import { useAppState } from 'store/Provider';
import { useHistory, useLocation } from 'react-router-dom';
import CustomerSelect from './CustomerSelect';
import { clearSelectedFilterAction } from 'store/actions/filterActions';

import { useResetFiltersAndDeliverables } from 'hooks/useResetFiltersAndDeliverables';

interface Props {
  name: string;
  isWelcome: boolean;
  subTitleElement?: React.ReactNode;
  handleClick: Dispatch<SetStateAction<boolean>>;
  isOpen: boolean;
}

interface LocationState {
  searchParamsPage: any;
  pathname: string;
  searchParam: string;
}

const MenuButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#FFFFFF',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const Topbar: React.FC<Props> = ({ name, isWelcome, subTitleElement, handleClick, isOpen }) => {
  const appState = useSelector((state) => state.app);
  const loadings = useSelector((state) => state.loadings);
  const { dispatch } = useAppState();
  const { resetFiltersAndDeliverables } = useResetFiltersAndDeliverables();
  const userIsConsultant = appState.roles?.toString().includes('consultant');
  const activeCustomer = useSelector((state) => state.app.customer);
  const userIsCustomer = appState.user?.is_client; // TODO use customer roles ?
  const conditionForlink = subTitleElement === undefined || (!userIsConsultant && !userIsCustomer);

  const [t] = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const handleLayoutActionButton = () => {
    const source_page: string | null = localStorage.getItem('from_source_page');
    const isBackButton = source_page === 'dashboard' || (conditionForlink && (userIsConsultant || userIsCustomer));
    return (
      <Button onClick={processUrls} startIcon={<ArrowBackIos />}>
        {isBackButton ? t('Back') : t('Management')}
      </Button>
    );
  };

  const processUrls = () => {
    const name = location?.state as LocationState;
    const source_page: string | null = localStorage.getItem('from_source_page');
    const isDeliverablesPath = location.pathname.includes('deliverables');
    const isAffectationPath = location.pathname.includes('affectation');
    const isChangebaseline = location.pathname.includes('changebaseline');
    const isNotDashboard = source_page !== 'dashboard';
    const isRelevantPath = isDeliverablesPath || isAffectationPath || isChangebaseline;

    if (isRelevantPath && isNotDashboard) {
      if (name) {
        history.push({
          pathname: name?.pathname, // Keep the current path
          search: name?.searchParam, // Keep the current query string
          state: name?.searchParamsPage, // Reset the state to null or an empty object if you prefer
        });
        dispatch(setPrevSearchParamsAction(null));
        dispatch(clearSelectedFilterAction());
      } else {
        resetFiltersAndDeliverables();
        history.goBack();
      }
    } else {
      resetFiltersAndDeliverables();
      history.push(`/${activeCustomer?.slug}/dashboard`);
    }
  };

  return (
    <Box
      sx={{
        mx: 4,
        mt: 4,
        pb: 4,
        display: 'flex',
        flexDirection: { md: 'row-reverse', xs: 'column' },
        justifyContent: 'space-between',
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 0 } }}>
        <Box>
          <MenuButton onClick={() => handleClick(!isOpen)} size="large" sx={{ display: { xs: 'flex', md: 'none' } }}>
            <Menu />
          </MenuButton>
        </Box>

        {appState.user ? <UserTopBar activeUser={appState.user as User} /> : <></>}

        {loadings.api ? (
          <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0 }} className="topbar-progress">
            <LinearProgress style={{ height: '3px' }} />
          </Box>
        ) : (
          <></>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
        }}
      >
        {/* Title + CustomerSelect row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* --- Page Title --- */}
          <Typography
            data-testid="topbar-title"
            variant="h1"
            color="neutral.main"
            sx={{
              fontSize: { xs: '2.2rem', md: '2.5rem' },
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {appState.user && appState.user.first_name && name}
          </Typography>

          {!location.pathname.includes('dashboard') &&
          !location.pathname.includes('documents') &&
          !location.pathname.includes('helpmenu') &&
          appState.user &&
          name ? (
            <>
              <Box
                sx={{
                  height: 28,
                  width: '1px',
                  backgroundColor: 'grey.400',
                  mx: 2,
                }}
              />
              <CustomerSelect />
            </>
          ) : null}
        </Box>

        {/* Optional Subtitle or Buttons */}
        {subTitleElement && <Box sx={{ ml: { xs: 0, md: 3 } }}>{subTitleElement}</Box>}
        {conditionForlink &&
          (name === 'Deliverables' || name === 'Assignment' || location.pathname.includes('changebaseline')) &&
          handleLayoutActionButton()}
      </Box>
    </Box>
  );
};
export default Topbar;
